import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges} from '@angular/core';
import {DEFAULT_STORE, IStore} from '../../../../shared/shared-models/store/store';
import {Store} from '@ngrx/store';
import {
  DEFAULT_LOADING_NOTIFICATION,
  DEFAULT_NOTIFICATIONS_CHIPS,
  DEFAULT_NOTIFICATIONS_PER_STORE,
  INotification,
  INotificationChips,
  INotificationPerStore,
  INotificationRead,
} from '../../models';
import {IScrollDataInput} from '../../../../shared/shared-module-ui/components/shared-scrolling-container/models';
import {NotificationChipTypes} from '../../models/notification-chips.enum';
import {NotificationTypeEnum, NotificationTypeEnumHumanReadable} from '../../models/notification-types.enum';
import {sortArrayByProperty} from '../../../../shared/shared-utils/sort/sort-array-by-property';
import {getDeepCopyOfObject, getShallowCopyOfObject} from '../../../../shared/shared-utils/object/object.utils';
import {
  dateToFirebaseTimestamp,
  firebaseTimeStampToDate,
} from '../../../../shared/shared-utils/firestore/firestore.utils';
import {
  batchUpdateNotifications,
  getAllNotificationsPaged,
  getNotificationAutoOrder,
  getNotificationStockUpdate,
  setNotificationFilterChips,
  setSelectedNotification,
  updateNotification,
} from '../../store/notification.actions';
import {IUser} from '../../../user-access/models';
import {ISelectOption} from '../../../../shared/shared-models/custom-select/custom-select';
import {IconsEnum} from '../../../../shared-modules/shared-icons/models/icons.enum';
import {IconColourEnum} from '../../../../shared-modules/shared-icons/models/icons-colour.enum';

const MAX_SPARSE_FILTER_AUTO_PAGES = 8;
const MAX_ALL_MESSAGES_AUTO_PAGES = 2;
const MIN_VISIBLE_FOR_AUTO_PAGE = 8;

const NOTIFICATION_TYPE_FILTERS: readonly NotificationTypeEnum[] = [
  NotificationTypeEnum.AUTO_ORDERS,
  NotificationTypeEnum.STOCK_UPDATE,
  NotificationTypeEnum.STOCK_TAKE,
  NotificationTypeEnum.QUOTES,
];

@Component({
    selector: 'app-notification-modal',
    templateUrl: './notification-modal.component.html',
    styleUrls: ['./notification-modal.component.scss'],
    standalone: false
})
export class NotificationModalComponent implements OnChanges {

  isAccordionOpen: Record<string, boolean> = {};
  isMobile: boolean;

  notifications: INotificationPerStore = getShallowCopyOfObject(DEFAULT_NOTIFICATIONS_PER_STORE);
  notificationChips: INotificationChips = getShallowCopyOfObject(DEFAULT_NOTIFICATIONS_CHIPS);
  filteredFailedCount = 0;

  selectedStores: string[] = [];

  @Input() filterChips: INotificationChips;
  @Input() isUserScrollLoading = true;
  @Input() lastDocumentId: string | null = null;
  @Input() notificationsPerStore: Record<string, INotificationPerStore> = {};
  @Input() stores: IStore[] = [];
  @Input() triggerMarkAllAsRead = false;
  @Input() user: IUser;
  @Input() userSelectedNotification: INotification;

  @Output() readonly storesChange = new EventEmitter<string[]>();

  protected readonly icons = IconsEnum;
  protected readonly iconColourEnum = IconColourEnum;
  protected readonly firebaseTimeStampToDate = firebaseTimeStampToDate;
  protected notificationTypesEnum = NotificationTypeEnum;
  private notificationsDisplay: INotification[] = [];
  protected readonly scrollData: IScrollDataInput<INotification[]> = {data: []};
  protected scrollDataRevision = 0;
  private pagingForShortList = false;
  private sparseFilterAutoPageCount = 0;
  private allMessagesAutoPageCount = 0;
  private lastScrollDataRevisionSignature = '';
  protected pageRequested = false;

  /**
   * Infinite scroll auto-fill is disabled; short lists are paged from this modal with a cap
   * so opening the panel does not fetch the entire notification history.
   */
  readonly enableScrollAutoFillWhenShort: boolean = false;

  constructor(
    private readonly store: Store,
  ) {
    this.isMobile = window.innerWidth <= 768;
  }

  get getIsUserScrollLoading(): boolean {
    return this.isUserScrollLoading;
  }

  get lastNotificationDocumentId(): string | null {
    return this.lastDocumentId;
  }



  get getNotifications(): INotificationPerStore {
    const userStores: IStore[] = this.getUserStores
      .filter((store: IStore) => store.storeId !== 'system');
    const selectedStores = this.getUserSelectedStores;
    const noticePerStore = {
      total: 0,
      read: 0,
      notifications: [],
      failed: 0,
      unread: 0,
      unreadFailed: 0,
    } as INotificationPerStore;
    if (userStores?.length > 0) {
      userStores.forEach((store: IStore) => {
        if (selectedStores.includes(store.storeId)) {
          const notice = this.notificationsPerStore[store.storeId];
          noticePerStore.total += notice?.total;
          noticePerStore.read += notice?.read;
          noticePerStore.unread += notice?.unread;
          noticePerStore.failed += notice?.failed;
          noticePerStore.unreadFailed += notice?.unreadFailed;
          noticePerStore.notifications = [
            ...noticePerStore?.notifications ?? [],
            ...notice?.notifications ?? [],
          ];
        }
      });
    }
    return noticePerStore;
  }

  get getUserSelectedStores(): string[] {
    return this.selectedStores;
  }

  get getUser(): IUser {
    return this.user;
  }

  get getUserStores(): IStore[] {
    return [...(this.stores ?? []), DEFAULT_STORE];
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['stores']) {
      this.selectedStores = this.getUserStores
        .filter((store: IStore) => store.storeId !== 'system')
        .map((store: IStore) => store.storeId);
      this.sparseFilterAutoPageCount = 0;
      this.allMessagesAutoPageCount = 0;
      this.pagingForShortList = false;
      this.pageRequested = false;
      this.notifications = this.getNotifications;
      this.onFilterNotifications();
      this.ensureValidFailedChip();
      this.storesChange.emit(this.selectedStores);
    }
    if (changes['isUserScrollLoading']) {
      this.syncScrollData();
      if (changes['isUserScrollLoading'].currentValue === false && !changes['notificationsPerStore']) {
        this.pagingForShortList = false;
        this.pageRequested = false;
        this.loadMoreWhileListIsShort();
      }
    }
    if (changes['notificationsPerStore']) {
      this.notificationsPerStore = changes.notificationsPerStore?.currentValue ?? {};
      this.pagingForShortList = false;
      this.pageRequested = false;
      this.notifications = this.getNotifications;
      this.onFilterNotifications();
      this.ensureValidFailedChip();
    }
    if (changes['triggerMarkAllAsRead']) {
      if (changes['triggerMarkAllAsRead'].currentValue === true) {
        this.triggerMarkAllAsRead = changes.triggerMarkAllAsRead.currentValue;
        this.markAllNotificationsAsRead();
      }
    }
    if (changes['filterChips']) {
      const nextChips: INotificationChips | null | undefined = changes.filterChips.currentValue;
      this.notificationChips = getShallowCopyOfObject(nextChips ?? DEFAULT_NOTIFICATIONS_CHIPS);
      this.sparseFilterAutoPageCount = 0;
      this.allMessagesAutoPageCount = 0;
      this.onFilterNotifications();
      this.ensureValidFailedChip();
    }
  }

  getAuthor(notification: INotification): string {
    if (
      notification.type === NotificationTypeEnum.AUTO_ORDERS ||
      notification.type === NotificationTypeEnum.LOADING ||
      notification.type === NotificationTypeEnum.QUOTES ||
      notification.type === NotificationTypeEnum.STOCK_UPDATE ||
      notification.type === NotificationTypeEnum.STOCK_TAKE
    ) {
      return 'System Generated';
    }
    return 'Gallix';
  }

  getHumanReadableNotificationType(notification: INotification): string {
    if (notification.type === NotificationTypeEnum.STOCK_UPDATE && notification.newStockItem) {
      return 'New Stock Item';
    }
    return NotificationTypeEnumHumanReadable[notification.type];
  }

  /**
   * Card title for the notification list, including success or failure.
   */
  getCardTitle(notification: INotification): string {
    const succeeded: boolean = notification.success === true && notification.reverted !== true;
    if (notification.type === NotificationTypeEnum.STOCK_UPDATE && notification.newStockItem) {
      return succeeded ? 'New Stock Item Added' : 'New Stock Item Failed';
    }
    if (notification.type === NotificationTypeEnum.STOCK_UPDATE) {
      return succeeded ? 'Stock Update Successful' : 'Stock Update Failed';
    }
    if (notification.type === NotificationTypeEnum.AUTO_ORDERS) {
      return succeeded ? 'Order Processed Successfully' : 'Order Processing Failed';
    }
    if (notification.type === NotificationTypeEnum.STOCK_TAKE) {
      return succeeded ? 'Stock Take Processed' : 'Stock Take Failed';
    }
    if (notification.type === NotificationTypeEnum.QUOTES) {
      return succeeded ? 'Quote Processed' : 'Quote Failed';
    }
    return this.getHumanReadableNotificationType(notification);
  }

  /**
   * Short status label shown beside the card title.
   */
  getStatusLabel(notification: INotification): string {
    if (notification.reverted) {
      return 'REVERTED';
    }
    if (notification.success) {
      return 'SUCCESS';
    }
    if (notification.retried) {
      return 'RETRIED';
    }
    return 'FAILED';
  }

  /**
   * Leading icon for the notification type.
   */
  getCardIcon(notification: INotification): IconsEnum {
    if (notification.type === NotificationTypeEnum.AUTO_ORDERS) {
      return IconsEnum.cart;
    }
    if (notification.type === NotificationTypeEnum.STOCK_TAKE) {
      return IconsEnum.clipboardCheckOutline;
    }
    if (notification.type === NotificationTypeEnum.QUOTES) {
      return IconsEnum.calculator;
    }
    return IconsEnum.create;
  }

  /**
   * Icon colour matching the success or failure state.
   */
  getCardIconColour(notification: INotification): IconColourEnum {
    if (notification.reverted) {
      return IconColourEnum.yellow;
    }
    if (notification.success) {
      return IconColourEnum.green;
    }
    return IconColourEnum.red;
  }

  /**
   * Store chip label. Server-generated events are marked as coming from the server.
   */
  getStoreChip(notification: INotification): string {
    const storeName: string = this.getStoreName(notification);
    if (this.getAuthor(notification) === 'System Generated') {
      return `${storeName}`;
    }
    return storeName;
  }

  getStoreName(notification: INotification): string {
    const store = this.getUserStores.find((store: IStore) => store.storeId === notification.storeId);
    return store?.name ? store.name : notification.storeId;
  }

  onStoreSelectionChange(selected: Record<string, ISelectOption>): void {
    this.selectedStores = [];
    Object.keys(selected)
      .filter(
        (storeId: string) => selected[storeId].selected,
      ).forEach((storeId: string) => {
      this.selectedStores.push(storeId);
    });
    this.sparseFilterAutoPageCount = 0;
    this.allMessagesAutoPageCount = 0;
    this.pagingForShortList = false;
    this.pageRequested = false;
    this.notifications = this.getNotifications;
    this.onFilterNotifications();
    this.ensureValidFailedChip();
    this.storesChange.emit(this.selectedStores);
  }

  onToggleClick(toggleValue: boolean, chip: NotificationChipTypes): void {
    switch (chip) {
      case 'FAIL':
        this.notificationChips.fail = toggleValue;
        this.sparseFilterAutoPageCount = 0;
        this.allMessagesAutoPageCount = 0;
        this.pagingForShortList = false;
        this.pageRequested = false;
        break;
      case 'UNREAD':
        this.notificationChips.unreadMessages = toggleValue;
        this.notificationChips.allMessages = !toggleValue;
        this.sparseFilterAutoPageCount = 0;
        this.allMessagesAutoPageCount = 0;
        this.pagingForShortList = false;
        this.pageRequested = false;
        break;
      case 'ALL':
        this.notificationChips.allMessages = toggleValue;
        this.notificationChips.unreadMessages = !toggleValue;
        this.notificationChips.fail = false;
        break;
      default:
      case 'READ':
      case 'SUCCESS':
        break;
    }
    this.store.dispatch(setNotificationFilterChips({
      filters: this.notificationChips,
    }));
    this.onFilterNotifications();
  }

  onNotificationTypeSelectionChange(selected: Record<string, ISelectOption>): void {
    this.notificationChips.autoOrder = !!selected[NotificationTypeEnum.AUTO_ORDERS]?.selected;
    this.notificationChips.stockUpdate = !!selected[NotificationTypeEnum.STOCK_UPDATE]?.selected;
    this.notificationChips.stockTake = !!selected[NotificationTypeEnum.STOCK_TAKE]?.selected;
    this.notificationChips.quotes = !!selected[NotificationTypeEnum.QUOTES]?.selected;

    this.store.dispatch(setNotificationFilterChips({
      filters: this.notificationChips,
    }));
    this.onFilterNotifications();
  }

  getNotificationTypesAsISelectOptions(): Record<string, ISelectOption> {
    return NOTIFICATION_TYPE_FILTERS.reduce((acc: Record<string, ISelectOption>, type: NotificationTypeEnum) => {
      acc[type] = {
        name: NotificationTypeEnumHumanReadable[type],
        selected: this.isNotificationTypeSelected(type),
        disabled: false,
      };
      return acc;
    }, {});
  }

  getStoresAsISelectOptions(): Record<string, ISelectOption> {
    const stores: IStore[] = getDeepCopyOfObject(this.stores) ?? [];
    return stores.reduce((acc: Record<string, ISelectOption>, store) => {
      acc[store.storeId] = {
        name: store.name,
        selected: this.selectedStores.includes(store.storeId),
        disabled: false,
      };
      return acc;
    }, {});
  }

  onScrollTrigger(): void {
    if (this.isUserScrollLoading || this.pageRequested) {
      return;
    }
    this.pageRequested = true;
    let selectedStores: string[];
    if (this.selectedStores?.length > 0) {
      selectedStores = this.getUserStores
        .filter((store: IStore) => store.storeId !== 'system')
        .filter((store: IStore) => this.selectedStores.includes(store.storeId))
        .map((store: IStore) => store.storeId);
    } else {
      selectedStores = this.getUserStores.map((store: IStore) => store.storeId);
    }

    this.store.dispatch(getAllNotificationsPaged({
      isLoggingIn: false,
      storeIds: selectedStores,
    }));
  }

  selectNotification(openOrClose: boolean, notification: INotification): void {
    if (this.userSelectedNotification) {
      this.isAccordionOpen[this.userSelectedNotification.notificationId] = false;
    }
    this.isAccordionOpen[notification.notificationId] = openOrClose;
    if (openOrClose) {
      switch (notification.type) {
        case 'AUTO_ORDERS':
        case NotificationTypeEnum.AUTO_ORDERS:
          this.store.dispatch(getNotificationAutoOrder({notification}));
          break;
        case 'STOCK_UPDATE':
        case NotificationTypeEnum.STOCK_UPDATE:
          this.store.dispatch(getNotificationStockUpdate({notification}));
          break;
        case 'LOADING':
        case NotificationTypeEnum.LOADING:
          break;
        case 'QUOTES':
        case NotificationTypeEnum.QUOTES:
          break;
        case 'STOCK_TAKE':
        case NotificationTypeEnum.STOCK_TAKE:
          break;
      }
    }

    if (this.userSelectedNotification && !this.isAccordionOpen[this.userSelectedNotification.notificationId]) {
      const notify = getDeepCopyOfObject(this.userSelectedNotification);
      notify.read = notify.read?.filter(Boolean) ?? [];
      const currentUserId = this.getUser.id;
      const hasUserRead = notify.read.some(
        (readEntry: INotificationRead) => readEntry.userId === currentUserId,
      );
      if (hasUserRead) {
        if (!notify.hasOwnProperty('isNotificationRead') || notify.isNotificationRead === false) {
          notify.isNotificationRead = true;
          this.store.dispatch(updateNotification({notification: notify}));
        }
      }
      if (!hasUserRead) {
        notify.read.push({
          userId: currentUserId,
          timestamp: dateToFirebaseTimestamp(new Date()),
        });
        notify.isNotificationRead = true;
        this.store.dispatch(updateNotification({notification: notify}));
      }

    }
    this.store.dispatch(setSelectedNotification({notification}));
  }

  private onFilterNotifications(): void {
    let notices: INotification[] = this.notifications.notifications ?? [];

    const allTypeChipsOn =
      this.notificationChips.autoOrder &&
      this.notificationChips.stockUpdate &&
      this.notificationChips.stockTake &&
      this.notificationChips.quotes;

    if (!allTypeChipsOn) {
      const allowed: string[] = [];
      if (this.notificationChips.autoOrder) allowed.push(NotificationTypeEnum.AUTO_ORDERS);
      if (this.notificationChips.stockUpdate) allowed.push(NotificationTypeEnum.STOCK_UPDATE);
      if (this.notificationChips.stockTake) allowed.push(NotificationTypeEnum.STOCK_TAKE);
      if (this.notificationChips.quotes) allowed.push(NotificationTypeEnum.QUOTES);
      notices = notices.filter((notice: INotification) => allowed.includes(notice.type));
    }

    this.filteredFailedCount = notices.filter((notice: INotification) => notice.success === false).length;

    if (this.notificationChips.fail) {
      notices = notices.filter((notice: INotification) => notice.success === false);
    }
    if (this.notificationChips.unreadMessages && !this.notificationChips.allMessages) {
      notices = notices.filter((notice: INotification) => !notice.read || notice.read?.length < 1);
    }
    const returnNotices: INotification[] = notices;

    const sortedNotifications = sortArrayByProperty(-1, false, 'end', [...returnNotices], 'timestamp', 'firebase-timestamp');
    this.notificationsDisplay = sortedNotifications ?? [];
    this.syncScrollData();
    this.loadMoreWhileListIsShort();
  }

  /**
   * Keeps scroll data reference stable so infinite scroll is not re-triggered on every change detection.
   * Revision tracks visible notification ids only — not loading state — so a page that adds no rows
   * does not reset short-list auto-fill when the fetch completes.
   */
  private syncScrollData(): void {
    const notifications: INotification[] = Array.isArray(this.notificationsDisplay) ? this.notificationsDisplay : [];
    const isLoading = this.getIsUserScrollLoading;
    if (isLoading) {
      this.scrollData.data = [...notifications, {...DEFAULT_LOADING_NOTIFICATION}];
    } else {
      this.scrollData.data = notifications;
    }
    const revisionSignature = this.buildScrollDataRevisionSignature(notifications);
    if (revisionSignature !== this.lastScrollDataRevisionSignature) {
      this.lastScrollDataRevisionSignature = revisionSignature;
      this.scrollDataRevision += 1;
    }
  }

  private buildScrollDataRevisionSignature(notifications: INotification[]): string {
    const length = notifications.length;
    if (length === 0) {
      return '0';
    }
    return `${length}:${notifications[0].notificationId}:${notifications[length - 1].notificationId}`;
  }

  private isAllMessagesView(): boolean {
    return this.notificationChips?.allMessages === true
      && !this.notificationChips?.fail
      && !this.notificationChips?.unreadMessages;
  }

  /**
   * Failed and unread rows are sparse in paged history. Request extra pages when either filter is on and the list is still short.
   */
  private isSparseFilteredView(): boolean {
    if (this.notificationChips?.fail) {
      return true;
    }
    return this.notificationChips?.unreadMessages === true && !this.notificationChips?.allMessages;
  }

  /**
   * When the visible list is shorter than the panel, request more pages up to a cap (scroll cannot trigger paging).
   */
  private loadMoreWhileListIsShort(): void {
    const sparseView = this.isSparseFilteredView();
    const allMessagesView = this.isAllMessagesView();
    if ((!sparseView && !allMessagesView) || this.pagingForShortList || this.pageRequested || this.isUserScrollLoading) {
      return;
    }
    const maxAutoPages = sparseView ? MAX_SPARSE_FILTER_AUTO_PAGES : MAX_ALL_MESSAGES_AUTO_PAGES;
    const autoPageCount = sparseView ? this.sparseFilterAutoPageCount : this.allMessagesAutoPageCount;
    if (autoPageCount >= maxAutoPages) {
      return;
    }
    if (!this.lastDocumentId || this.notificationsDisplay.length >= MIN_VISIBLE_FOR_AUTO_PAGE) {
      return;
    }
    this.pagingForShortList = true;
    if (sparseView) {
      this.sparseFilterAutoPageCount += 1;
    } else {
      this.allMessagesAutoPageCount += 1;
    }
    this.onScrollTrigger();
  }

  private isNotificationTypeSelected(type: NotificationTypeEnum): boolean {
    switch (type) {
      case NotificationTypeEnum.AUTO_ORDERS:
        return this.notificationChips.autoOrder;
      case NotificationTypeEnum.STOCK_UPDATE:
        return this.notificationChips.stockUpdate;
      case NotificationTypeEnum.STOCK_TAKE:
        return this.notificationChips.stockTake;
      case NotificationTypeEnum.QUOTES:
        return this.notificationChips.quotes;
      default:
        return false;
    }
  }

  private ensureValidFailedChip(): void {
    if (this.notificationChips?.fail && this.filteredFailedCount <= 0) {
      this.notificationChips.fail = false;
      this.onFilterNotifications();
      this.store.dispatch(setNotificationFilterChips({
        filters: this.notificationChips,
      }));
    }
  }

  private markAllNotificationsAsRead(): void {
    const currentUserId = this.getUser.id;
    const unread = (Array.isArray(this.notificationsDisplay) ? this.notificationsDisplay : []).filter(
      (notification: INotification | null | undefined): notification is INotification =>
        !!notification
        && (!notification.read || notification.read.length === 0)
        || notification.isNotificationRead === false,
    );
    if (unread.length > 0) {
      const updatedNotifications = unread.map((notification: INotification) => ({
        ...notification,
        isNotificationRead: true,
        read: [
          ...[],
          {
            userId: currentUserId,
            timestamp: dateToFirebaseTimestamp(new Date()),
          },
        ],
      }));
      this.store.dispatch(batchUpdateNotifications({
        notifications: updatedNotifications,
        merge: true,
      }));
    }
    this.triggerMarkAllAsRead = false;
  }

  getTimeAgo(date: Date | string): string {
    const now = new Date();
    const past = new Date(date);

    const diffMs = now.getTime() - past.getTime();
    const diffMinutes = Math.floor(diffMs / 60000);

    if (diffMinutes < 1) {
      return 'Just now';
    }

    if (diffMinutes < 60) {
      return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
    }

    const diffHours = Math.floor(diffMinutes / 60);

    if (diffHours < 24) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    }

    const diffDays = Math.floor(diffHours / 24);

    return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  }

}
